<template>
  <DialogWrapper
    hideFooter
    class="job-application-dialog"
    saveBtnLabelI18nKey="common.btn.confirm"
    :title="$t(`${i18nPrefix}.label.testCandidate`)"
    :closeOnClickModal="false"
    :loading="isLoading"
    :show.sync="isShow"
  >
    <div v-show="testJobFitResult.candidateName" class="test-candidate-application-content">
      <div class="info-card">
        <div class="candidate-name">
          <span class="title">{{ $t(`${i18nPrefix}.label.candidateName`) }}</span>
          <span>{{ testJobFitResult.candidateName }}</span>
        </div>
        <div class="test-result">
          <span class="title">{{ $t(`${i18nPrefix}.label.testResultWithColon`) }}</span>
          <span class="summary">{{ testJobFitResult.summaryI18nKey }}</span>
        </div>
        <div v-show="testJobFitResult.score" class="score-container">
          <div v-auto-tooltip.always="$t('admin.application.candidate.tip.resumeFitInfo')">
            <div ref="fitScore" class="chart-score"></div>
          </div>
      </div>
      </div>
    </div>
    <div class="job-application-list">
      <TableList
        ref="elTable"
        tableHeight="400px"
        row-key="key"
        :showCreateBtn="false"
        :list-data="jobApplicationList"
        :table-columns="tableColumns"
        :pagination="pagination"
        @pagination-change="loadJobApplicationList(false)"
      >
        <template #filter>
          <el-input
            v-model="search.keywords"
            clearable
            class="candidate-status-keywords"
            :placeholder="$t('admin.application.applicantList.placeholder.keywordsSearch')"
            @input="handleKeywordsChange"
          ></el-input>
        </template>
        <template #action-btn="{ row }">
          <el-button class="test-btn" @click="handleTestJobFitCandidate(row)">{{ $t(`${i18nPrefix}.label.test`) }}</el-button>
        </template>
      </TableList>
    </div>
  </DialogWrapper>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import * as echarts from 'echarts'
import { generateFitEchartsOption } from '@/constant/application'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import TableList from '@/components/tableList/listTableHasPage.vue'
import { AsyncTask, getJobFitScoreColor, formatJobFitScore } from '@/utils/common'
import { addI18nProperty, getI18nPropertyValue, getConstantI18nValue } from '@/i18n'
import { DEGREE } from '@/constant/meta'

export default {
  components: {
    DialogWrapper,
    TableList
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    jobId: {
      type: [String, Number],
      required: true
    },
    jobFitCondition: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.job.editor',
      jobApplicationList: [],
      search: {
        keywords: null
      },
      pagination: {
        total: 1,
        page: 1,
        pageSize: 10
      },
      testJobFitResult: {
        score: null,
        summaryEn: null,
        summaryCn: null,
        candidateName: null
      }
    }
  },
  computed: {
    isShow: {
      get () {
        return this.show
      },
      set (val) {
        this.$emit('update:show', val)
      }
    },
    isLoading: {
      get () {
        return this.loading
      },
      set (val) {
        this.$emit('update:loading', val)
      }
    },
    tableColumns () {
      const i18nPrefix = 'admin.application.applicantList'
      const columns = [
        {
          labelI18nKey: `${i18nPrefix}.label.candidateName`,
          dataKey: 'candidate.name',
          formatter: (candidateName, application) => {
            if (application.is_agency_uploaded_and_hid) {
              this.$t(`${i18nPrefix}.label.hidedCandidateName`)
            }
            return candidateName
          }
        },
        {
          label: this.$t(`${i18nPrefix}.label.workCompanyName`),
          dataKey: 'recent_work_experience.company'
        },
        {
          label: this.$t(`${i18nPrefix}.label.lastWorkJobName`),
          dataKey: 'recent_work_experience.job'
        },
        {
          label: this.$t(`${i18nPrefix}.label.workExperienceYear`),
          dataKey: 'candidate.total_work_year',
          cellClass: 'title-column',
          formatter: (totalWorkYear) => {
            if (!totalWorkYear) {
              return ''
            }
            let totalWorkYearI18nName = ''
            if (totalWorkYear < 1) {
              totalWorkYearI18nName = `${_.ceil(totalWorkYear * 12)} ${this.$t(`${i18nPrefix}.label.month`)}`
            } else {
              totalWorkYearI18nName = `${Math.floor(totalWorkYear * 10) / 10} ${this.$t(`${i18nPrefix}.label.year`)}`
            }
            return totalWorkYearI18nName
          }
        },
        {
          label: this.$t(`${i18nPrefix}.label.degree`),
          dataKey: 'candidate.max_degree',
          cellClass: 'title-column',
          formatter: (degree) => getConstantI18nValue(DEGREE, degree, 'meta.DEGREE')
        },
        {
          label: this.$t(`${i18nPrefix}.label.location`),
          dataKey: 'candidate.location',
          formatter: (location) => {
            const province = getI18nPropertyValue(location.province, 'cn_name', 'name')
            const city = getI18nPropertyValue(location.city, 'cn_name', 'name')
            return city || province
          }
        }
      ]
      return columns
    }
  },
  created () {

  },
  methods: {
    ...mapActions('application', ['getJobApplicationList']),
    ...mapActions('jobFit', ['createTestJobFitConditionTask', 'getTestJobFitConditionTask']),
    handleKeywordsChange () {
      if (!this.keywordsSearchDebounce) {
        this.keywordsSearchDebounce = _.debounce(() => this.loadJobApplicationList(true), 1000)
      }
      this.keywordsSearchDebounce()
    },
    async refreshData () {
      this.isLoading = true
      this.search = {
        keywords: null
      }
      await this.loadJobApplicationList(true)
    },
    async loadJobApplicationList (reload) {
      this.isLoading = true
      if (reload) {
        this.pagination.page = 1
        this.pagination.total = 0
      }
      await this.$nextTick()
      try {
        const params = {
          jobId: this.jobId,
          params: {
            keywords: this.search.keywords,
            order: '-apply_time',
            page: this.pagination.page,
            page_size: this.pagination.pageSize
          }
        }
        const resp = await this.getJobApplicationList(params)
        if (resp) {
          this.pagination.total = resp.total_count
          this.jobApplicationList = resp.data
        }
      } finally {
        this.isLoading = false
      }
    },
    async handleTestJobFitCandidate (row) {
      this.isLoading = true
      const aiTask = {
        task: null,
        isFinished: false
      }
      const params = {
        jobId: this.jobId,
        testInfo: {
          candidate_id: row.candidate.id,
          job_fit_condition: this.jobFitCondition
        }
      }
      aiTask.task = new AsyncTask(
        () => this.createTestJobFitConditionTask(params),
        (taskId) => this.getTestJobFitConditionTask({ jobId: this.jobId, taskId }),
        (taskId, resp) => {
          if (resp) {
            aiTask.isFinished = true
            const result = resp.result
            this.testJobFitResult.score = result.resume_ai_match_score
            this.testJobFitResult.summaryCn = result.resume_ai_match_summary_cn
            this.testJobFitResult.summaryEn = result.resume_ai_match_summary_en
            addI18nProperty(this.testJobFitResult, 'summaryI18nKey', 'summaryCn', 'summaryEn')
            this.initFitScore()
            this.testJobFitResult.candidateName = row.candidate.name
            this.isLoading = false
          }
        })
    },
    initFitScore () {
      const el = this.$refs.fitScore
      if (!el) {
        return
      }
      const val = this.testJobFitResult.score
      const color = getJobFitScoreColor(val) || ''
      const displayValue = formatJobFitScore(val) || val
      const option = generateFitEchartsOption(val, displayValue, color, 14)
      const series = option.series
      series.axisLine.lineStyle.width = 2
      const chart = echarts.init(el)
      chart.setOption(option)
      this.$nextTick(() => {
        chart.resize()
      })
    }
  },
  watch: {
    show () {
      if (!this.show) {
        this.testJobFitResult = {
          score: null,
          summaryEn: null,
          summaryCn: null,
          candidateName: null
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.job-application-dialog {
  .test-candidate-application-content {
    .info-card {
      margin-top: 20px;
      padding: 24px 40px 40px;
      background: #fff;
      border: 1px solid #e4e7ed;
      border-radius: 4px;
      box-shadow: 0px 2px 4px -1px rgba(0,0,0,0.08);
      position: relative;

      .test-result {
        margin-top: 20px;
        .summary {
          word-break: keep-all;
        }
      }

      .score-container {
        position: absolute;
        right: 20px;
        top: 20px;
        .chart-score {
          display: inline-block;
          vertical-align: top;
          width: 28px;
          height: 28px;
        }
      }
    }
  }
  .job-application-list {
    margin-top: 20px;
    .gender {
      margin-left: 2px;
    }
    ::v-deep {
      .filter-container {
        margin-right: -24px;
      }
      .title-column {
        .cell {
          word-break: keep-all;
          white-space: normal;
          text-overflow: ellipsis;
        }
      }
      .test-btn {
        // el-button默认有focus状态，需要重置样式。但是hover也会失效，所以需要重写一个hover
        border-color: #0096cc;
        color: #0096cc;
        background-color: #ffffff;

        &:hover {
          background-color: #e5f3f9;
        }
      }
    }
  }
}
</style>
